/**
 * MatchFilters — vertical filter sidebar for the Matches page.
 *
 * Renders search, Time / Status / Type / Sport chip groups stacked
 * below the calendar in the left column. Replaces the old horizontal
 * FilterBar that used to sit above the layout.
 *
 * Source: design_handoff_tank/screens-matches.jsx § Screen03Matches.
 */

import { useMemo } from 'react';
import clsx from 'clsx';
import { EVENT_TYPES, SPORTS } from '../../utils/constants';
import styles from './MatchFilters.module.css';

const TIME_RANGES = [
  { key: 'today', label: 'Today' },
  { key: 'week', label: 'Week' },
  { key: 'month', label: 'Month' },
  { key: 'all', label: 'All' },
];

const STATUS_OPTIONS = [
  { key: 'all', label: 'All' },
  { key: 'upcoming', label: 'Upcoming' },
  { key: 'live', label: 'Live' },
  { key: 'finished', label: 'Finished' },
];

const ASSIGNMENT_OPTIONS = [
  { key: 'all', label: 'All' },
  { key: 'mine', label: 'Mine' },
  { key: 'unassigned', label: 'Unassigned' },
];

function FilterPill({ active, onClick, color, children }) {
  return (
    <button
      className={clsx(styles.pill, active && styles.pillActive)}
      onClick={onClick}
      style={active && color ? { borderColor: color, color } : undefined}
      type="button"
    >
      {children}
    </button>
  );
}

function FilterSection({ label, children }) {
  return (
    <div className={styles.section}>
      <span className={styles.sectionLabel}>{label}</span>
      <div className={styles.sectionOptions}>{children}</div>
    </div>
  );
}

export default function MatchFilters({
  filters,
  onChange,
  onClear,
  role = 'viewer',
}) {
  const {
    timeRange = 'week',
    status = 'all',
    assignment = 'all',
    eventTypes = [],
    sport = null,
    search = '',
  } = filters;

  const updateFilter = (key, value) => {
    onChange({ ...filters, [key]: value });
  };

  const eventTypeEntries = useMemo(
    () => Object.entries(EVENT_TYPES).map(([key, val]) => ({ key, ...val })),
    []
  );

  const sportEntries = useMemo(
    () => Object.entries(SPORTS).map(([key, val]) => ({ key, ...val })),
    []
  );

  const showAssignmentFilter = role === 'COMPANY_MANAGER' || role === 'PLANNER';

  const hasActiveFilters =
    timeRange !== 'week' ||
    status !== 'all' ||
    assignment !== 'all' ||
    eventTypes.length > 0 ||
    sport !== null ||
    search !== '';

  const toggleEventType = (key) => {
    if (eventTypes.includes(key)) {
      updateFilter('eventTypes', eventTypes.filter((k) => k !== key));
    } else {
      updateFilter('eventTypes', [...eventTypes, key]);
    }
  };

  return (
    <div className={styles.container}>
      {/* Filter / Search */}
      <FilterSection label="Filter">
        <div className={styles.searchWrapper} style={{ width: '100%' }}>
          <input
            type="text"
            className={styles.searchInput}
            placeholder="Search team or competition"
            value={search}
            onChange={(e) => updateFilter('search', e.target.value)}
          />
          {search && (
            <button
              className={styles.clearSearch}
              onClick={() => updateFilter('search', '')}
              aria-label="Clear search"
              type="button"
            >
              ×
            </button>
          )}
        </div>
      </FilterSection>

      {/* Time */}
      <FilterSection label="Time">
        {TIME_RANGES.map((range) => (
          <FilterPill
            key={range.key}
            active={timeRange === range.key}
            onClick={() => updateFilter('timeRange', range.key)}
          >
            {range.label}
          </FilterPill>
        ))}
      </FilterSection>

      {/* Status */}
      <FilterSection label="Status">
        {STATUS_OPTIONS.map((opt) => (
          <FilterPill
            key={opt.key}
            active={status === opt.key}
            onClick={() => updateFilter('status', opt.key)}
          >
            {opt.label}
          </FilterPill>
        ))}
      </FilterSection>

      {/* Type */}
      <FilterSection label="Type">
        <FilterPill
          active={eventTypes.length === 0}
          onClick={() => updateFilter('eventTypes', [])}
        >
          All
        </FilterPill>
        {eventTypeEntries.map((type) => (
          <FilterPill
            key={type.key}
            active={eventTypes.includes(type.key)}
            color={type.color}
            onClick={() => toggleEventType(type.key)}
          >
            {type.label}
          </FilterPill>
        ))}
      </FilterSection>

      {/* Assigned (role-gated) */}
      {showAssignmentFilter && (
        <FilterSection label="Assigned">
          {ASSIGNMENT_OPTIONS.map((opt) => (
            <FilterPill
              key={opt.key}
              active={assignment === opt.key}
              onClick={() => updateFilter('assignment', opt.key)}
            >
              {opt.label}
            </FilterPill>
          ))}
        </FilterSection>
      )}

      {/* Sport */}
      <FilterSection label="Sport">
        <select
          className={styles.select}
          value={sport || ''}
          onChange={(e) => updateFilter('sport', e.target.value || null)}
        >
          <option value="">All Sports</option>
          {sportEntries.map((s) => (
            <option key={s.key} value={s.key}>
              {s.label}
            </option>
          ))}
        </select>
      </FilterSection>

      {hasActiveFilters && onClear && (
        <button className={styles.clearAll} onClick={onClear} type="button">
          Clear all filters
        </button>
      )}
    </div>
  );
}
